class StayveKitBuilder {
  constructor(root) {
    this.root = root;
    this.requiredQuantity =
      Number(root.dataset.requiredQuantity) || 10;

    this.maximumPerProduct =
      Number(root.dataset.maximumPerProduct) || 5;

    this.productCards = Array.from(
      root.querySelectorAll("[data-stayve-product]")
    );

    this.countOutputs = Array.from(
      root.querySelectorAll("[data-stayve-count]")
    );

    this.progressBars = Array.from(
      root.querySelectorAll("[data-stayve-progress]")
    );

    this.progressFills = Array.from(
      root.querySelectorAll("[data-stayve-progress-fill]")
    );

    this.instructions = Array.from(
      root.querySelectorAll("[data-stayve-instruction]")
    );

    this.selectionOutput =
      root.querySelector("[data-stayve-selection]");

    this.addButton =
      root.querySelector("[data-stayve-add]");

    this.clearButton =
      root.querySelector("[data-stayve-clear]");

    this.message =
      root.querySelector("[data-stayve-message]");

    this.quantities = new Map();

    this.productCards.forEach((card) => {
      this.quantities.set(card.dataset.variantId, 0);
    });

    this.bindEvents();
    this.updateInterface();
  }

  bindEvents() {
    this.productCards.forEach((card) => {
      const plusButton =
        card.querySelector("[data-stayve-plus]");

      const minusButton =
        card.querySelector("[data-stayve-minus]");

      plusButton?.addEventListener("click", () => {
        this.increaseProduct(card);
      });

      minusButton?.addEventListener("click", () => {
        this.decreaseProduct(card);
      });
    });

    this.clearButton?.addEventListener("click", () => {
      this.clearKit();
    });

    this.addButton?.addEventListener("click", () => {
      this.addKitToCart();
    });
  }

  getTotalQuantity() {
    return Array.from(this.quantities.values()).reduce(
      (total, quantity) => total + quantity,
      0
    );
  }

  getProductQuantity(card) {
    return (
      this.quantities.get(card.dataset.variantId) || 0
    );
  }

  getProductLimit(card) {
    const stockLimit =
      Number(card.dataset.stockLimit);

    if (Number.isFinite(stockLimit)) {
      return Math.min(
        stockLimit,
        this.maximumPerProduct
      );
    }

    return this.maximumPerProduct;
  }

  increaseProduct(card) {
    const totalQuantity =
      this.getTotalQuantity();

    const currentQuantity =
      this.getProductQuantity(card);

    const productLimit =
      this.getProductLimit(card);

    const isAvailable =
      card.dataset.available === "true";

    if (!isAvailable) {
      this.showMessage(
        "This ampoule is currently unavailable.",
        true
      );
      return;
    }

    if (totalQuantity >= this.requiredQuantity) {
      this.showMessage(
        `Your kit can contain a maximum of ${this.requiredQuantity} ampoules.`,
        true
      );
      return;
    }

    if (currentQuantity >= productLimit) {
      this.showMessage(
        `You can choose a maximum of ${productLimit} of this ampoule.`,
        true
      );
      return;
    }

    this.quantities.set(
      card.dataset.variantId,
      currentQuantity + 1
    );

    this.showMessage("");
    this.updateInterface();
  }

  decreaseProduct(card) {
    const currentQuantity =
      this.getProductQuantity(card);

    if (currentQuantity <= 0) {
      return;
    }

    this.quantities.set(
      card.dataset.variantId,
      currentQuantity - 1
    );

    this.showMessage("");
    this.updateInterface();
  }

  clearKit() {
    this.quantities.forEach((value, variantId) => {
      this.quantities.set(variantId, 0);
    });

    this.showMessage("");
    this.updateInterface();
  }

  updateInterface() {
    const totalQuantity =
      this.getTotalQuantity();

    const remainingQuantity =
      Math.max(
        this.requiredQuantity - totalQuantity,
        0
      );

    const progressPercentage =
      Math.min(
        (totalQuantity / this.requiredQuantity) * 100,
        100
      );

    this.countOutputs.forEach((output) => {
      output.textContent =
        `${totalQuantity} / ${this.requiredQuantity}`;
    });

    this.progressBars.forEach((progressBar) => {
      progressBar.setAttribute(
        "aria-valuenow",
        String(totalQuantity)
      );
    });

    this.progressFills.forEach((fill) => {
      fill.style.width =
        `${progressPercentage}%`;
    });

    this.instructions.forEach((instruction) => {
      if (totalQuantity === this.requiredQuantity) {
        instruction.textContent =
          "Your kit is complete.";
      } else {
        instruction.textContent =
          `Choose ${remainingQuantity} more ${
            remainingQuantity === 1
              ? "ampoule"
              : "ampoules"
          } to complete your kit.`;
      }
    });

    this.productCards.forEach((card) => {
      const quantity =
        this.getProductQuantity(card);

      const productLimit =
        this.getProductLimit(card);

      const quantityOutput =
        card.querySelector(
          "[data-stayve-quantity]"
        );

      const plusButton =
        card.querySelector(
          "[data-stayve-plus]"
        );

      const minusButton =
        card.querySelector(
          "[data-stayve-minus]"
        );

      const isAvailable =
        card.dataset.available === "true";

      if (quantityOutput) {
        quantityOutput.textContent =
          String(quantity);
      }

      if (minusButton) {
        minusButton.disabled =
          quantity <= 0;
      }

      if (plusButton) {
        plusButton.disabled =
          !isAvailable ||
          totalQuantity >=
            this.requiredQuantity ||
          quantity >= productLimit;
      }

      card.classList.toggle(
        "is-selected",
        quantity > 0
      );
    });

    this.updateSelectionSummary();

    if (this.addButton) {
      const kitComplete =
        totalQuantity ===
        this.requiredQuantity;

      this.addButton.disabled =
        !kitComplete;

      this.addButton.textContent =
        kitComplete
          ? "Add kit to bag"
          : `Choose ${this.requiredQuantity} ampoules`;
    }

    if (this.clearButton) {
      this.clearButton.disabled =
        totalQuantity === 0;
    }
  }

  updateSelectionSummary() {
    if (!this.selectionOutput) {
      return;
    }

    const selectedProducts =
      this.productCards
        .map((card) => {
          const quantity =
            this.getProductQuantity(card);

          if (quantity <= 0) {
            return null;
          }

          return {
            title:
              card.dataset.productTitle,
            quantity
          };
        })
        .filter(Boolean);

    if (selectedProducts.length === 0) {
      this.selectionOutput.innerHTML = `
        <p class="stayve-inline-builder__selection-empty">
          Your selected ampoules will appear here.
        </p>
      `;

      return;
    }

    this.selectionOutput.innerHTML =
      selectedProducts
        .map(
          (product) => `
            <div class="stayve-inline-builder__selection-item">
              <span>${this.escapeHtml(product.title)}</span>
              <strong>× ${product.quantity}</strong>
            </div>
          `
        )
        .join("");
  }

  escapeHtml(value) {
    const element =
      document.createElement("div");

    element.textContent =
      String(value);

    return element.innerHTML;
  }

  showMessage(text, isError = false) {
    if (!this.message) {
      return;
    }

    this.message.textContent = text;

    this.message.classList.toggle(
      "is-error",
      isError
    );
  }

  getCartItems() {
    return this.productCards
      .map((card) => {
        const quantity =
          this.getProductQuantity(card);

        if (quantity <= 0) {
          return null;
        }

        return {
          id: Number(
            card.dataset.variantId
          ),
          quantity,
          properties: {
            "_Custom kit":
              "Build Your Stayve Kit"
          }
        };
      })
      .filter(Boolean);
  }

  async addKitToCart() {
    const totalQuantity =
      this.getTotalQuantity();

    if (
      totalQuantity !==
      this.requiredQuantity
    ) {
      this.showMessage(
        `Please choose exactly ${this.requiredQuantity} ampoules.`,
        true
      );

      return;
    }

    const items =
      this.getCartItems();

    if (!items.length) {
      return;
    }

    this.addButton.disabled = true;
    this.addButton.textContent =
      "Adding your kit…";

    this.showMessage("");

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            items
          })
        }
      );

      const responseData =
        await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.description ||
          responseData.message ||
          "Your kit could not be added."
        );
      }

      this.addButton.textContent =
        "Added to your bag";

      this.showMessage(
        "Your custom Stayve kit has been added."
      );

    const currentCartLink = document.querySelector(
  'a[href="/cart"], a[href$="/cart"]'
);

const headerSection = currentCartLink?.closest(".shopify-section");

if (currentCartLink && headerSection?.id) {
  const sectionId = headerSection.id.replace(
    "shopify-section-",
    ""
  );

  const sectionResponse = await fetch(
    `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`
  );

  const sections = await sectionResponse.json();
  const sectionHtml = sections[sectionId];

  if (sectionHtml) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(
      sectionHtml,
      "text/html"
    );

    const newCartLink = newDocument.querySelector(
      'a[href="/cart"], a[href$="/cart"]'
    );

    if (newCartLink) {
      currentCartLink.innerHTML = newCartLink.innerHTML;
    }
  }
}

      // await this.openCartDrawer();

      window.setTimeout(() => {
        this.addButton.disabled = false;
        this.addButton.textContent =
          "Add kit to bag";
      }, 1500);
    } catch (error) {
      console.error(
        "Stayve kit error:",
        error
      );

      this.showMessage(
        error.message ||
          "Something went wrong. Please refresh and try again.",
        true
      );

      this.addButton.disabled = false;
      this.addButton.textContent =
        "Add kit to bag";
    }
  }

  async openCartDrawer() {
    const cartDrawer =
      document.querySelector(
        "cart-drawer"
      ) ||
      document.querySelector(
        "[data-cart-drawer]"
      ) ||
      document.querySelector(
        ".cart-drawer"
      );

    if (
      cartDrawer &&
      typeof cartDrawer.open ===
        "function"
    ) {
      // cartDrawer.open();
      return;
    }

    const cartButton =
      document.querySelector(
        "[data-cart-drawer-trigger]"
      ) ||
      document.querySelector(
        'button[aria-controls*="cart" i]'
      ) ||
      document.querySelector(
        'a[aria-controls*="cart" i]'
      ) ||
      document.querySelector(
        'a[href$="/cart"]'
      );

    if (cartButton) {
      // cartButton.click();
      return;
    }

    // window.location.href =
//   `${window.Shopify.routes.root}cart`;
  }
}

function initialiseStayveKitBuilders(
  container = document
) {
  const builders =
    container.querySelectorAll(
      "[data-stayve-builder]"
    );

  builders.forEach((builder) => {
    if (
      builder.dataset.stayveInitialised ===
      "true"
    ) {
      return;
    }

    builder.dataset.stayveInitialised =
      "true";

    new StayveKitBuilder(builder);
  });
}

document.addEventListener(
  "DOMContentLoaded",
  () => {
    initialiseStayveKitBuilders();
  }
);

document.addEventListener(
  "shopify:section:load",
  (event) => {
    initialiseStayveKitBuilders(
      event.target
    );
  }
);

initialiseStayveKitBuilders();